import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Loader2, Plus, Trash2, Mail, Save } from 'lucide-react';

const SETTING_KEY = 'support_ticket_config';

export default function SupportTicketsConfigTab() {
  const qc = useQueryClient();
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['platform_settings', SETTING_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings').select('value').eq('key', SETTING_KEY).maybeSingle();
      if (error) throw error;
      return (data?.value as unknown as { recipient_emails?: string[] }) ?? null;
    },
  });

  useEffect(() => { if (data) setEmails(data.recipient_emails ?? []); }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('platform_settings')
        .upsert({ key: SETTING_KEY, value: { recipient_emails: emails } as unknown as Json }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', SETTING_KEY] });
      toast({ title: 'Configuración guardada' });
    },
    onError: () => toast({ title: 'Error al guardar', variant: 'destructive' }),
  });

  const addEmail = () => {
    const t = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) { toast({ title: 'Correo inválido', variant: 'destructive' }); return; }
    if (emails.includes(t)) { toast({ title: 'Este correo ya está en la lista', variant: 'destructive' }); return; }
    setEmails([...emails, t]); setNewEmail('');
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" />Avisos de tickets de soporte</CardTitle>
        <CardDescription>Cada vez que un usuario cree un ticket de soporte, recibirán un correo los destinatarios configurados aquí.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input placeholder="correo@empresa.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addEmail()} className="max-w-sm" />
          <Button variant="outline" size="sm" onClick={addEmail}><Plus className="w-4 h-4 mr-1" />Agregar</Button>
        </div>
        {emails.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin destinatarios. Los tickets se crearán pero no se enviará aviso por correo.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {emails.map((email) => (
              <Badge key={email} variant="secondary" className="gap-1.5 pr-1.5">
                {email}
                <button onClick={() => setEmails(emails.filter((e) => e !== email))} className="hover:text-destructive transition-colors">
                  <Trash2 className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <div className="flex justify-end pt-2 border-t border-border">
          <Button onClick={() => save.mutate()} disabled={save.isPending} size="sm">
            {save.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
